import type { Metric } from "@/components/metric-counters";

/** Per-product hero metrics. Numbers are illustrative — edit freely. */
export const productMetrics: Record<string, Metric[]> = {
  "market-intelligence": [
    { value: 10000, suffix: "+", label: "signals analysed / week", proof: "per customer" },
    { value: 14, suffix: "d", label: "earlier than competitors", proof: "median lead time" },
    { value: 4, label: "narratives surfaced / day", proof: "ranked by intent" },
  ],
  "ai-visibility-analytics": [
    { value: 11, suffix: "×", label: "AI citations & mentions", proof: "vs. baseline" },
    { value: 4, label: "LLMs tracked live", proof: "ChatGPT · Claude · Gemini · Perplexity" },
    { value: 92, suffix: "%", label: "share-of-voice tracked", proof: "vs. competitor set" },
  ],
  "cross-channel-social-analytics": [
    { value: 5, suffix: "×", label: "engagement uplift", proof: "founder accounts" },
    { value: 1, suffix: " dashboard", label: "for every channel", proof: "no more tab juggling" },
    { value: 38, prefix: "+", suffix: "%", label: "qualified pipeline", proof: "median, 90d" },
  ],
  "geo-aeo-seo-autopilot": [
    { value: 11, suffix: "×", label: "AI citations & mentions", proof: "vs. baseline" },
    { value: 40, prefix: "+", suffix: "%", label: "organic traffic", proof: "90-day median" },
    { value: 30, suffix: " +", label: "GEO articles / month", proof: "fully autonomous" },
  ],
  "linkedin-autopilot": [
    { value: 5, suffix: "×", label: "engagement rate", proof: "vs. manual posting" },
    { value: 284, suffix: "k", label: "monthly reach", proof: "founder + brand combined" },
    { value: 7, label: "posts / week", proof: "live trend-informed" },
  ],
  "x-autopilot": [
    { value: 6.5, suffix: "×", label: "engagement rate", proof: "vs. manual posting" },
    { value: 192, suffix: "k", label: "monthly impressions", proof: "median customer" },
    { value: 12, label: "posts + threads / week", proof: "narrative-aware" },
  ],
};

/** Per-use-case hero metrics. */
const STANDARD_TWO: Metric[] = [
  { value: 11, suffix: "×", label: "AI visibility", proof: "across ChatGPT, Gemini, Claude, Perplexity" },
  { value: 40, prefix: "+", suffix: "%", label: "traffic from LLMs", proof: "vs. baseline" },
];

export const useCaseMetrics: Record<string, Metric[]> = {
  law: [
    ...STANDARD_TWO,
    { value: 3.4, suffix: "×", label: "qualified inbound", proof: "first 90 days" },
  ],
  cybersecurity: [
    ...STANDARD_TWO,
    { value: 48, suffix: "h", label: "from threat → published POV", proof: "median response" },
  ],
  "real-estate-construction": [
    ...STANDARD_TWO,
    { value: 41, prefix: "+", suffix: "%", label: "qualified showings", proof: "90-day median" },
  ],
  logistics: [
    ...STANDARD_TWO,
    { value: 33, prefix: "+", suffix: "%", label: "RFP invites", proof: "first 6 months" },
  ],
  web3: [
    ...STANDARD_TWO,
    { value: 62, prefix: "+", suffix: "%", label: "community engagement", proof: "30-day window" },
  ],
  fitness: [
    ...STANDARD_TWO,
    { value: 48, prefix: "+", suffix: "%", label: "social reach", proof: "monthly" },
  ],
  ecommerce: [
    ...STANDARD_TWO,
    { value: 3.2, suffix: "×", label: "ROAS on social", proof: "narrative-aware" },
  ],
  "high-growth-startups": [
    ...STANDARD_TWO,
    { value: 1, label: "marketing FTE replaced", proof: "founder-led teams" },
  ],
  "b2b-saas": [
    { value: 11, suffix: "×", label: "AI visibility", proof: "across ChatGPT, Gemini, Claude, Perplexity" },
    { value: 40, prefix: "+", suffix: "%", label: "traffic from LLMs", proof: "vs. baseline" },
    { value: 20, prefix: "+", suffix: "%", label: "qualified pipeline from LLMs", proof: "SaaS customers" },
  ],
  "mid-market-enterprise": [
    ...STANDARD_TWO,
    { value: 26, prefix: "+", suffix: "%", label: "share of voice", proof: "vs. peer set" },
  ],
  "venture-studios": [
    ...STANDARD_TWO,
    { value: 60, prefix: "−", suffix: "%", label: "marketing setup time", proof: "per new co" },
  ],
  "agencies-studios": [
    ...STANDARD_TWO,
    { value: 3.1, suffix: "×", label: "agency margin uplift", proof: "vs. retainer model" },
  ],
};

/** Module slug to render in the live-product slot for each use-case. */
export const useCaseModule: Record<string, string> = {
  law: "ai-visibility-analytics",
  cybersecurity: "market-intelligence",
  "real-estate-construction": "geo-aeo-seo-autopilot",
  logistics: "geo-aeo-seo-autopilot",
  web3: "x-autopilot",
  fitness: "linkedin-autopilot",
  ecommerce: "ai-visibility-analytics",
  "high-growth-startups": "linkedin-autopilot",
  "b2b-saas": "geo-aeo-seo-autopilot",
  "mid-market-enterprise": "ai-visibility-analytics",
  "venture-studios": "market-intelligence",
  "agencies-studios": "cross-channel-social-analytics",
};
